<script setup lang="ts">
import {Appointment} from "@/core/interfaces/Appointment";
import {computed, ref} from "vue";
import TimelineItem from "@/appointment/components/consumer-appointment-timeline/TimelineItem.vue";
import AgendaAppointmentDetail from "@/agenda/components/AgendaAppointmentDetail.vue";
import {AgendaAppointment} from "@/core/interfaces/AgendaAppointment";
import {toAgendaAppointment} from "@/agenda/lib/toAgendaAppointment";

const MAX_APPOINTMENT_PER_DAY = 2;
const SECTION_WIDTH = 300; // px per section

const props = defineProps<{
    appointments: Appointment[]
}>();

defineEmits<{
    /** An appointment was cancelled, moved or edited from its detail. */
    (event: 'reload'): void
}>();

/** One detail for the whole timeline, whichever card opened it. */
const detailOpen = ref(false);
const detailAppointment = ref<AgendaAppointment | null>(null);

const openDetail = (appointment: Appointment) => {
    detailAppointment.value = toAgendaAppointment(appointment);
    detailOpen.value = true;
};

const sortedAppointments = computed(() => {
    return [...props.appointments]
        .sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
});

const appointmentsByDate = computed(() => {
    const grouped = new Map<string, Appointment[]>();

    sortedAppointments.value.forEach(appointment => {
        const date = new Date(appointment.start_at).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit'
        });

        if (!grouped.has(date)) {
            grouped.set(date, []);
        }

        grouped.get(date)!.push(appointment);
    });

    return Array.from(grouped.entries()).map(([date, appointments]) => ({
        date,
        appointments,
        displayedAppointments: appointments.slice(0, MAX_APPOINTMENT_PER_DAY),
        hasMoreAppointments: appointments.length > MAX_APPOINTMENT_PER_DAY
    }));
});

const totalWidth = computed(() => {
    return appointmentsByDate.value.length * SECTION_WIDTH;
});
</script>

<template>
  <div class="w-full">
    <div class="relative overflow-x-auto bg-white overflow-y-hidden">
      <div class="relative" :style="{ width: `${totalWidth}px`, minWidth: '100%' }">
        <div class="relative flex items-center mb-2">
          <div class="absolute top-1/2 left-0 w-full h-1 bg-primary transform -translate-y-1/2" />

          <div class="flex relative z-10">
            <div
              v-for="(group) in appointmentsByDate"
              :key="`circle-${group.date}`"
              class="flex-shrink-0"
              :style="{ width: `${SECTION_WIDTH}px` }"
            >
              <div class="flex flex-col items-center">
                <div class="w-6 h-6 mt-5 rounded-full bg-white border-[1px] border-primary flex items-center justify-center" />
                <p class="text-sm font-medium text-center leading-tight mt-1">
                  {{ group.date }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="flex">
          <div
            v-for="(group) in appointmentsByDate"
            :key="`bottom-${group.date}`"
            class="flex-shrink-0 px-3 pb-2"
            :style="{ width: `${SECTION_WIDTH}px` }"
          >
            <div class="h-full flex flex-col justify-start">
              <div class="space-y-3 flex flex-col">
                <template v-for="(appointment) in group.displayedAppointments" :key="appointment.id">
                  <TimelineItem :appointment="appointment" @select="openDetail(appointment)" />
                </template>
              </div>

              <div
                v-if="group.hasMoreAppointments"
                class="text-xs text-primary text-center mt-2 bg-white rounded-full px-3 py-1.5 font-medium"
              >
                +{{ group.appointments.length - MAX_APPOINTMENT_PER_DAY }} autres
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Mounted on the first click: its dialogs load the team and the types. -->
    <AgendaAppointmentDetail
      v-if="detailAppointment"
      v-model:open="detailOpen"
      :appointment="detailAppointment"
      @changed="$emit('reload')"
    />
  </div>
</template>

<style scoped>
.overflow-x-auto::-webkit-scrollbar {
    height: 6px;
}

.overflow-x-auto::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
    background-color: #cbd5e0;
    border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
    background-color: #a0aec0;
}

.overflow-x-auto {
    scroll-behavior: smooth;
}

.space-y-3 > * {
    animation: fadeInUp 0.3s ease-out;
    animation-fill-mode: both;
}

.space-y-3 > *:nth-child(1) { animation-delay: 0.1s; }
.space-y-3 > *:nth-child(2) { animation-delay: 0.2s; }
.space-y-3 > *:nth-child(3) { animation-delay: 0.3s; }

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (max-width: 768px) {
    .overflow-x-auto {
        padding: 8px;
    }
}
</style>
